import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Container,
  Stack,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Popover,
  Paper,
  Zoom,
  Slide,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  ViewQuilt,
  Slideshow,
  Image as ImageIcon,
  Description,
  Settings,
  Download,
  ArrowForward,
  Celebration,
  ReportProblem,
  AutoAwesome,
  Close,
  ArrowDownward,
  ArrowDropDown,
} from '@mui/icons-material';

import { useProject } from '../hooks/useProject';
import { ProjectTab } from '../components/projects/ProjectTabPanels';
import MaterialList from '../components/projects/MaterialList';
import { useAppContext } from '../contexts/AppContext';
import SlidesEditor from '../components/projects/SlidesEditor';
import { useLang } from '../contexts/langContext';
import { useBranding } from '../contexts/BrandingContext';
import OutlineManager from '../components/projects/OutlineManager';
import SlideSlicer, { SlideSlicerRef } from '../components/projects/SlideSlicer';
import Loading from '../components/common/Loading';
import { exportProjectAsPPTX } from '../services/projects';
import MaterialSearchResults from '../components/materials/MaterialSearchResults';
import DownloadIcon from '@mui/icons-material/Download';
import ProjectImageManager from '../components/projects/ProjectImageManager';
import { projectDetailOnboardingTips } from '../config/onboardingTips';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`project-tabpanel-${index}`} aria-labelledby={`project-tab-${index}`} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ProjectDetailPage: React.FC = () => {
  const { lang } = useLang();
  const branding = useBranding();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(parseInt(searchParams.get('tab') || '0'));
  const [isSuggestingSlideNumbers, setIsSuggestingSlideNumbers] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMissingSlideNumbersDialogOpen, setIsMissingSlideNumbersDialogOpen] = useState(false);
  const [exportStatus, setExportStatus] = useState<'idle' | 'exporting' | 'success' | 'error'>('idle');
  const [exportMessage, setExportMessage] = useState('');
  // Alert for custom outline hint
  const [customOutlineAlert, setCustomOutlineAlert] = useState(false);
  const { setAppTitle } = useAppContext();

  // Refs for onboarding tour
  const outlineRef = useRef<HTMLDivElement | null>(null);
  const slidesRef = useRef<HTMLDivElement | null>(null);
  const exportBtnRef = useRef<HTMLButtonElement | null>(null);
  const tabImagesRef = useRef<HTMLDivElement | null>(null);
  const tabMaterialsRef = useRef<HTMLDivElement | null>(null);
  const tabSettingsRef = useRef<HTMLDivElement | null>(null);
  const missingTemplateAlertRef = useRef<HTMLDivElement | null>(null);
  const slideSlicerRef = useRef<SlideSlicerRef | null>(null);

  // Onboarding tour state
  const [tourOpen, setTourOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tourSteps, setTourSteps] = useState<
    Array<{
      ref: React.RefObject<HTMLElement> | { current: HTMLElement | null };
      key: string;
      title: { en: string; 'zh-CN': string };
      description: { en: string; 'zh-CN': string };
    }>
  >([]);

  const showOnboardingTips = branding.defaultPreferences?.showOnboardingTips;
  const enableImageGeneration = branding.features?.enableImageGeneration;

  // Determine if we should auto-generate outline based on navigation state
  const autoGenerateOutline = (location.state as any)?.fromProjectCreation === true && (location.state as any)?.outlineOption === 'auto';

  const {
    project,
    handleUpdateProject,
    handleUpdateSlides,
    handleDeleteProject,
    handleSuggestPptxSlideNumber,
    handleSuggestPptxSlideNumbers,
    handleGenerateSlideData,
    handleSuggestSlideElementValue,
    handleRecommendSlideDescription,
  } = useProject(id!);

  useEffect(() => {
    if (project) {
      setAppTitle(project.title);
    }
  }, [project?.title, setAppTitle]);

  // Show alert for custom outline option with empty outline
  useEffect(() => {
    if (location.state && 'outlineOption' in location.state && location.state.outlineOption === 'custom' && project && project.slides.length === 0) {
      setCustomOutlineAlert(true);
    }
  }, [location.state, project?.slides.length]);

  // Auto-click SlideSlicer trigger when project has verbatimMaterialId and empty slides
  useEffect(() => {
    if (project && project.verbatimMaterialId && project.slides.length === 0 && slideSlicerRef.current) {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        slideSlicerRef.current?.triggerClick();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [project?.verbatimMaterialId, project?.slides.length]);

  useEffect(() => {
    if (location.state) {
      console.log('Navigation state received:', location.state);
      if ('outlineOption' in location.state) {
        if (location.state.outlineOption === 'docx' && project && project.slides.length > 0) {
          // Check if any slides are missing pptxSlideNumber
          const unassignedSlides = project.slides.filter((slide) => slide.pptxSlideNumber === undefined);

          if (unassignedSlides.length > 0) {
            // Only suggest templates for slides that don't have one assigned
            const allSlideNumbers = project.slides.map((_, index) => index + 1);
            console.log('Auto-suggesting templates for DOCX outline slides:', allSlideNumbers);

            setIsSuggestingSlideNumbers(true);
            handleSuggestPptxSlideNumbers(allSlideNumbers)
              .then((templateSlideNumbers) => {
                console.log('Auto template suggestion completed for DOCX outline:', templateSlideNumbers);

                const updatedSlides = [...project.slides];
                let hasUpdates = false;

                allSlideNumbers.forEach((slideNumber, i) => {
                  const slideIndex = slideNumber - 1; // Convert to 0-based index
                  const templateNumber = templateSlideNumbers[i];

                  // Only update slides that don't already have a pptxSlideNumber
                  if (templateNumber && templateNumber > 0 && updatedSlides[slideIndex].pptxSlideNumber === undefined) {
                    updatedSlides[slideIndex] = {
                      ...updatedSlides[slideIndex],
                      pptxSlideNumber: templateNumber,
                      aiSuggestionReasonForPptxSlideNumber: lang === 'en' ? 'Auto AI suggestion for DOCX outline' : 'DOCX大纲自动AI建议',
                    };
                    hasUpdates = true;
                  }
                });

                if (hasUpdates) {
                  handleUpdateSlides(updatedSlides);
                  console.log('Auto template suggestions applied and saved to project');
                }
              })
              .catch((error) => {
                console.error('Auto template suggestion failed for DOCX outline:', error);
              })
              .finally(() => {
                setIsSuggestingSlideNumbers(false);
              });
          }
        }
      }
    }
  }, [location.state, project?._id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSearchParams({ tab: newValue.toString() }, { replace: true });
  };

  const handleExportPPTX = async () => {
    // Validate that all slides have a pptxSlideNumber assigned
    const missingSlideNumbers = project?.slides.filter((slide) => slide.pptxSlideNumber === undefined);
    if (missingSlideNumbers && missingSlideNumbers.length > 0) {
      console.warn('Warning: Some slides are missing pptxSlideNumber.');
      setIsMissingSlideNumbersDialogOpen(true);
      return;
    }
    await exportPPTX();
  };

  const exportPPTX = async () => {
    try {
      setExportStatus('exporting');
      setExportMessage(lang === 'en' ? 'Exporting presentation...' : '正在导出演示文稿...');

      const blob = await exportProjectAsPPTX(id!);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${project?.title}.pptx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);

      setExportStatus('success');
      setExportMessage(lang === 'en' ? 'Export successful!' : '导出成功！');
    } catch (error) {
      console.error('Error exporting project:', error);
      setExportStatus('error');
      setExportMessage(lang === 'en' ? 'Error exporting presentation' : '导出演示文稿时出错');
    }
  };

  const handleCloseSnackbar = () => {
    setExportStatus('idle');
  };

  const handleCloseCustomOutlineAlert = () => {
    setCustomOutlineAlert(false);
  };

  // Tour handlers
  const handleNextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      finishTour();
    }
  };

  const finishTour = () => {
    localStorage.setItem('slideMaster_projectDetailOnboardingDone', 'true');
    setTourOpen(false);
    setCurrentStep(0);
  };

  // Initialize onboarding tour
  useEffect(() => {
    if (!project || !showOnboardingTips || tourOpen || tourSteps.length > 0) return;

    const onboardingDone = localStorage.getItem('slideMaster_projectDetailOnboardingDone');
    if (!onboardingDone) {
      // Build tour steps based on available elements
      const steps: Array<{
        ref: React.RefObject<HTMLElement> | { current: HTMLElement | null };
        key: string;
        title: { en: string; 'zh-CN': string };
        description: { en: string; 'zh-CN': string };
      }> = [];

      // Order: Outline → Slides → Images tab → Materials tab → Settings tab → Export button
      if (project.pptxFileId) {
        if (outlineRef.current) {
          steps.push({
            ref: outlineRef,
            key: 'outline',
            title: projectDetailOnboardingTips.outline.title,
            description: projectDetailOnboardingTips.outline.description,
          });
        }

        if (slidesRef.current) {
          steps.push({
            ref: slidesRef,
            key: 'slides',
            title: projectDetailOnboardingTips.slides.title,
            description: projectDetailOnboardingTips.slides.description,
          });
        }
      } else if (missingTemplateAlertRef.current) {
        steps.push({
          ref: missingTemplateAlertRef,
          key: 'missingTemplate',
          title: projectDetailOnboardingTips.missingTemplate.title,
          description: projectDetailOnboardingTips.missingTemplate.description,
        });
      }

      if (tabImagesRef.current) {
        const description = enableImageGeneration
          ? projectDetailOnboardingTips.images.description
          : {
              en: 'Manage images for this project. Upload your own pictures for perfect visual content.',
              'zh-CN': '管理此项目的图片。上传您自己的图片，获得完美的视觉内容。',
            };
        steps.push({
          ref: tabImagesRef,
          key: 'images',
          title: projectDetailOnboardingTips.images.title,
          description,
        });
      }

      if (tabMaterialsRef.current) {
        steps.push({
          ref: tabMaterialsRef,
          key: 'materials',
          title: projectDetailOnboardingTips.materials.title,
          description: projectDetailOnboardingTips.materials.description,
        });
      }

      if (tabSettingsRef.current) {
        steps.push({
          ref: tabSettingsRef,
          key: 'settings',
          title: projectDetailOnboardingTips.settings.title,
          description: projectDetailOnboardingTips.settings.description,
        });
      }

      // Export button is last (only if slides exist)
      if (exportBtnRef.current && project.slides.length > 0) {
        steps.push({
          ref: exportBtnRef,
          key: 'export',
          title: projectDetailOnboardingTips.export.title,
          description: projectDetailOnboardingTips.export.description,
        });
      }

      if (steps.length > 0) {
        // Small delay to ensure refs are populated
        setTimeout(() => {
          setTourSteps(steps);
          setTourOpen(true);
          setCurrentStep(0);
        }, 500);
      }
    }
  }, [project, showOnboardingTips, tourOpen, tourSteps.length, enableImageGeneration]);

  if (!project) {
    return <Loading />;
  }

  const emptySlides = project.slides.length === 0;

  // If we're on the search page, render the search results
  const isMaterialsSearch = location.pathname.endsWith('/materials/search');
  if (isMaterialsSearch) {
    return <MaterialSearchResults projectId={id} materials={[]} searchQuery="" />;
  }

  return (
    <Container sx={{ flexGrow: 1 }} maxWidth="lg">
      <Stack direction="row" alignItems="center">
        {/* icon button back */}
        <IconButton onClick={() => window.history.back()} edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Box flexGrow={1} />
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="project tabs" variant="standard" centered>
          <Tab label={lang === 'en' ? 'Slides' : '幻灯片'} />
          <Tab ref={tabImagesRef} label={lang === 'en' ? 'Images' : '图片'} />
          <Tab ref={tabMaterialsRef} label={lang === 'en' ? 'Materials' : '材料'} />
          <Tab ref={tabSettingsRef} label={lang === 'en' ? 'Settings' : '设置'} />
        </Tabs>
        <Box flexGrow={1} />
        {!emptySlides && (
          <Button
            ref={exportBtnRef}
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportPPTX}
            disabled={exportStatus === 'exporting'}
          >
            {lang === 'en' ? 'Build PPT' : '生成PPT'}
          </Button>
        )}
      </Stack>

      <TabPanel value={tabValue} index={0}>
        {project.pptxFileId ? (
          <Stack spacing={2} mt={emptySlides ? 3 : 0}>
            {/* Custom outline hint alert */}
            <Container sx={{ display: 'flex', justifyContent: 'center' }}>
              <Slide direction="down" in={customOutlineAlert} mountOnEnter unmountOnExit>
                <Paper
                  elevation={5}
                  sx={{
                    mb: 2,
                    maxWidth: 'sm',
                    p: 3,
                    border: '2px solid',
                    borderColor: 'ai.light',
                    position: 'relative',
                  }}
                >
                  <IconButton
                    onClick={handleCloseCustomOutlineAlert}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: 'ai.light',
                    }}
                    size="small"
                  >
                    <Close />
                  </IconButton>

                  <Stack alignItems="center" spacing={2}>
                    <AutoAwesome
                      sx={{
                        fontSize: 40,
                        color: 'ai.light',
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        textAlign: 'center',
                        color: 'ButtonText',
                        lineHeight: 1.5,
                      }}
                    >
                      {lang === 'en'
                        ? 'To create a custom outline, click the "Generate Outline" button below or add materials and images first to provide context for AI generation.'
                        : '要创建自定义大纲，请点击下方的"生成大纲"按钮，或先添加材料和图片来为幻灯片项目补充素材。'}
                    </Typography>
                    <ArrowDropDown sx={{ color: 'text.disabled', fontSize: 40 }} />
                  </Stack>
                </Paper>
              </Slide>
            </Container>
            <Box ref={outlineRef}>
              {!!project.verbatimMaterialId ? (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: Array.isArray(project.slides) && project.slides.length > 0 ? 'flex-start' : 'center',
                  }}
                >
                  <SlideSlicer ref={slideSlicerRef} project={project} onUpdateSlides={handleUpdateSlides} />
                </Box>
              ) : (
                <OutlineManager project={project} onUpdateProject={handleUpdateProject} autoGenerate={autoGenerateOutline} />
              )}
            </Box>
            <Box ref={slidesRef}>
              <SlidesEditor
                key={project.updatedAt.toString()}
                project={project}
                slides={[...project.slides]}
                onUpdateSlides={handleUpdateSlides}
                onSuggestPptxSlideNumber={handleSuggestPptxSlideNumber}
                onSuggestPptxSlideNumbers={handleSuggestPptxSlideNumbers}
                onSuggestSlideData={handleGenerateSlideData}
                onSuggestSlideElementValue={handleSuggestSlideElementValue}
                onRecommendSlideDescription={handleRecommendSlideDescription}
              />
            </Box>
            {/* AI content warning */}
            {!emptySlides && (
              <Typography variant="caption" align="center" color="text.secondary" gutterBottom>
                {lang === 'en'
                  ? 'The content generated by AI is not guaranteed to be accurate. Please review and edit the content before using it.'
                  : 'AI生成的内容不保证准确性。请在生成PPT之前仔细审查和编辑内容。'}
              </Typography>
            )}
          </Stack>
        ) : (
          <Alert
            ref={missingTemplateAlertRef}
            severity="warning"
            action={
              <Button color="inherit" onClick={() => setTabValue(3)}>
                {lang === 'en' ? 'Go to Settings' : '去设置'}
              </Button>
            }
          >
            {lang === 'en' ? 'Choose a PPTX template first' : '先选择一个PPTX模板'}
          </Alert>
        )}
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <ProjectImageManager projectId={project._id} />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <MaterialList projectId={project._id} project={project} onUpdateProject={handleUpdateProject} />
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        <ProjectTab
          project={project}
          onEdit={handleUpdateProject}
          onDelete={() => setIsDeleteDialogOpen(true)}
          onPptxFileChange={async (fileId) => {
            await handleUpdateProject({ pptxFileId: fileId });
          }}
        />
      </TabPanel>

      <Snackbar
        open={exportStatus !== 'idle'}
        autoHideDuration={exportStatus === 'success' || exportStatus === 'error' ? 6000 : null}
        onClose={handleCloseSnackbar}
        message={
          <Stack direction="row" spacing={1} alignItems="center">
            {exportStatus === 'exporting' && <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />}
            {exportMessage}
          </Stack>
        }
      />

      <Dialog
        open={isSuggestingSlideNumbers}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6">{lang === 'en' ? 'Suggesting slide templates...' : '正在推荐幻灯片模板...'}</Typography>
        </Box>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Project</DialogTitle>
        <DialogContent>
          {lang === 'en' ? 'Are you sure you want to delete this project? This action cannot be undone.' : '你确定要删除这个项目吗？此操作无法撤销。'}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteProject} color="error" autoFocus>
            {lang === 'en' ? 'Delete' : '删除'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isMissingSlideNumbersDialogOpen}
        onClose={() => setIsMissingSlideNumbersDialogOpen(false)}
        aria-labelledby="missing-slide-numbers-dialog-title"
        aria-describedby="missing-slide-numbers-dialog-description"
      >
        <DialogTitle id="missing-slide-numbers-dialog-title">{lang === 'en' ? 'Warning: Missing Slide Numbers' : '警告：缺少幻灯片编号'}</DialogTitle>
        <DialogContent>
          {lang === 'en'
            ? 'Some slides are missing PPTX slide numbers. This may result in an incomplete export. Do you want to continue anyway?'
            : '一些幻灯片缺少PPTX幻灯片编号。这可能导致导出不完整。你想继续吗？'}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsMissingSlideNumbersDialogOpen(false)}>{lang === 'en' ? 'Cancel' : '取消'}</Button>
          <Button
            onClick={() => {
              setIsMissingSlideNumbersDialogOpen(false);
              exportPPTX();
            }}
            color="warning"
            autoFocus
          >
            {lang === 'en' ? 'Export Anyway' : '仍然导出'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Onboarding Tour Popover */}
      {tourOpen && tourSteps.length > 0 && tourSteps[currentStep] && (
        <Popover
          open={tourOpen}
          anchorEl={tourSteps[currentStep]?.ref.current}
          onClose={() => {}}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          disableRestoreFocus
          disableAutoFocus
          disableEnforceFocus
          sx={{
            pointerEvents: 'none',
            '& .MuiPopover-paper': {
              pointerEvents: 'auto',
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              maxWidth: 360,
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -8,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderBottom: '8px solid white',
              },
            },
          }}
        >
          <Zoom in={tourOpen} timeout={300}>
            <Paper sx={{ p: 3, position: 'relative' }} elevation={4}>
              {/* Progress indicator */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  display: 'flex',
                  gap: 0.5,
                }}
              >
                {tourSteps.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: index === currentStep ? 'primary.main' : 'grey.300',
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
              </Box>

              {/* Content */}
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 4 }}>
                  {tourSteps[currentStep].key === 'outline' && <ViewQuilt sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  {tourSteps[currentStep].key === 'slides' && <Slideshow sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  {tourSteps[currentStep].key === 'images' && <ImageIcon sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  {tourSteps[currentStep].key === 'materials' && <Description sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  {tourSteps[currentStep].key === 'settings' && <Settings sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  {tourSteps[currentStep].key === 'export' && <Download sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  {tourSteps[currentStep].key === 'missingTemplate' && <ReportProblem sx={{ color: 'primary.main', fontSize: '1.3rem' }} />}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1.1rem',
                    }}
                  >
                    {tourSteps[currentStep].title[lang as 'en' | 'zh-CN']}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                  }}
                >
                  {tourSteps[currentStep].description[lang as 'en' | 'zh-CN']}
                </Typography>

                {/* Actions */}
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                  <Button size="small" onClick={finishTour} sx={{ color: 'text.secondary' }}>
                    {lang === 'en' ? 'Skip All' : '跳过全部'}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={handleNextStep}
                    endIcon={currentStep === tourSteps.length - 1 ? <Celebration /> : <ArrowForward />}
                  >
                    {currentStep === tourSteps.length - 1 ? (lang === 'en' ? 'Get Started!' : '开始使用！') : lang === 'en' ? 'Next' : '下一步'}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Zoom>
        </Popover>
      )}
    </Container>
  );
};

export default ProjectDetailPage;
